import React, {useState} from "react";
import {HeaderCard} from "../Base/HeaderCard";
import {NavigationHeader} from "../Navigation/NavigationHeader";
import {useTranslation} from "react-i18next";
import {useParams, useNavigate} from "react-router-dom";
import {Card, CardHeader, CardActionArea, CardContent, Typography, Grid, Box, Dialog, DialogContent, DialogActions, Button, Divider} from "@mui/material";
import classes from '../../styling/components/DemoDashboard.module.css'
import {DemoMailOverviewCard} from "../Demo/DemoMailOverviewCard";
import {DemoTemplatePreviewCard} from "../Demo/DemoTemplatePreviewCard";
import {DemoDispatch} from "../../model/DemoDispatch";
import {DemoTemplatePreview} from "../../model/DemoTemplatePreview";
import {EmailDisplay} from "../Template/TemplatePreview/EmailDisplay";

interface Props {
    demoDispatches: DemoDispatch[];
    demoTemplatesPreview: DemoTemplatePreview[];
}

export const DemoDashboardOverview: React.FC<Props> = ({ demoDispatches, demoTemplatesPreview }) => {
    const {t} = useTranslation()
    const {customerId} = useParams()
    const navigate = useNavigate()
    const [selectedDispatch, setSelectedDispatch] = useState<DemoDispatch | null>(null);
    const [selectedTemplate, setSelectedTemplate] = useState<DemoTemplatePreview | null>(null);

    const navigation = [
        {url: "", label: t("dashboard.title")}
    ]

    const hasDispatches = demoDispatches.length > 0;
    const itemsToShow = hasDispatches ? demoDispatches : demoTemplatesPreview;

    const handleOpenDispatch = (dispatch: DemoDispatch) => {
        setSelectedDispatch(dispatch);
    };

    const handleOpenTemplate = (template: DemoTemplatePreview) => {
        setSelectedTemplate(template);
    };

    const handleCloseDialog = () => {
        setSelectedDispatch(null);
        setSelectedTemplate(null);
    };

    const handleOpenLearningPage = () => {
        const templateId = selectedDispatch?.templateId || selectedTemplate?.templateId;
        if (!templateId || !customerId) {
            return;
        }
        const previewUrl = `/customers/${customerId}/templates/${templateId}/preview`;
        window.open(previewUrl, "_blank", "noopener,noreferrer");
    };

    const handleOpenLearningPageFromCard = (item: DemoDispatch | DemoTemplatePreview) => {
        const templateId = item.templateId;
        if (!templateId || !customerId) {
            return;
        }
        const previewUrl = `/customers/${customerId}/templates/${templateId}/preview`;
        window.open(previewUrl, "_blank", "noopener,noreferrer");
    };

    const sortedDemoDispatches = hasDispatches 
        ? [...demoDispatches].sort((a, b) =>
            new Date(a.sentAt).getTime() - new Date(b.sentAt).getTime()
        )
        : [];

    const sortedTemplates = !hasDispatches 
        ? [...demoTemplatesPreview].sort((a, b) => a.name.localeCompare(b.name))
        : [];

    const selectedItem = selectedDispatch || selectedTemplate;

    return (
        <>
            <NavigationHeader path={navigation}/>
            <HeaderCard
                headline={<>{t('dashboard.welcomeHeader')}</>}
                body={<>{t('dashboard.welcomeText')}</>}
            />

            {itemsToShow.length > 0 && <Box className={classes.sectionSpacingAfterHeader}>
                <Card className={`${classes.card} ${classes.mailListCard}`}>
                    <CardHeader
                        title={hasDispatches ? t("dashboard.recentDemoMails") : t("dashboard.demoTemplatesOverview")}
                        titleTypographyProps={{
                            variant: "h5",
                            component: "h2",
                            sx: {
                                fontFamily: '"Oswald", "Roboto", sans-serif',
                                fontWeight: 'bold',
                                color: '#424242'
                            }
                        }}
                    />
                    <CardContent>
                        {hasDispatches ? (
                            sortedDemoDispatches.map((demoMail, index) => (
                                <React.Fragment key={index}>
                                    <DemoMailOverviewCard 
                                        demoMail={demoMail} 
                                        onClick={() => handleOpenDispatch(demoMail)}
                                        onLearningPageClick={() => handleOpenLearningPageFromCard(demoMail)}
                                    />
                                    {index < sortedDemoDispatches.length - 1 && <Divider className={classes.mailListDivider} />}
                                </React.Fragment>
                            ))
                        ) : (
                            sortedTemplates.map((template, index) => (
                                <React.Fragment key={index}>
                                    <DemoTemplatePreviewCard 
                                        template={template} 
                                        onClick={() => handleOpenTemplate(template)}
                                        onLearningPageClick={() => handleOpenLearningPageFromCard(template)}
                                    />
                                    {index < sortedTemplates.length - 1 && <Divider className={classes.mailListDivider} />}
                                </React.Fragment>
                            ))
                        )}
                    </CardContent>
                </Card>
            </Box>}
            
            <Box className={classes.sectionSpacing}>
                <Grid container spacing={3}>
                    <Grid item xs={12} md={6}>
                        <Card className={`${classes.card} ${classes.featureCard}`}>
                            <CardActionArea className={classes.featureCardAction} onClick={() => navigate(`/customers/${customerId}/demo/elearning`)}>
                                <CardContent className={classes.cardContent}>
                                    <div className={classes.cardLayout}>
                                        <div className={classes.contentArea}>
                                            <div className={classes.titleContainer}>
                                                <Typography variant="h6" component="h3" className={classes.title}>
                                                    {t("dashboard.elearningCard.title")}
                                                </Typography>
                                            </div>
                                            <Typography variant="body2" color="text.secondary" className={classes.content}>
                                                {t("dashboard.elearningCard.description")}
                                            </Typography>
                                        </div>
                                        <div className={classes.imageArea}>
                                            <img src="/assets/img/ElearningPicture.png" alt="E-learning preview" className={classes.previewImage} />
                                        </div>
                                    </div>
                                </CardContent>
                            </CardActionArea>
                        </Card>
                    </Grid>
                    <Grid item xs={12} md={6}>
                        <Card className={`${classes.card} ${classes.featureCard}`}>
                            <CardActionArea className={classes.featureCardAction} onClick={() => navigate(`/customers/${customerId}/demo/analytics`)}>
                                <CardContent className={classes.cardContent}>
                                    <div className={classes.cardLayout}>
                                        <div className={classes.contentArea}>
                                            <div className={classes.titleContainer}>
                                                <Typography variant="h6" component="h3" className={classes.title}>
                                                    {t("dashboard.analyticsCard.title")}
                                                </Typography>
                                            </div>
                                            <Typography variant="body2" color="text.secondary" className={classes.content}>
                                                {t("dashboard.analyticsCard.description")}
                                            </Typography>
                                        </div>
                                        <div className={classes.imageArea}>
                                            <object type="image/svg+xml" data="/assets/img/AnalyticsPicture.svg" className={classes.previewImage} aria-label="Analytics preview" />
                                        </div>
                                    </div>
                                </CardContent>
                            </CardActionArea>
                        </Card>
                    </Grid>
                </Grid>
            </Box>

            <Dialog
                open={Boolean(selectedItem)}
                onClose={handleCloseDialog}
                fullWidth
                maxWidth="md"
            >
                <DialogContent dividers>
                    {selectedItem && (
                        <EmailDisplay
                            filledSenderAddress={selectedDispatch?.sender || selectedTemplate?.sender || ''}
                            filledSubject={selectedDispatch?.subject || selectedTemplate?.subject || ''}
                            filledBody={selectedDispatch?.body || selectedTemplate?.body || ''}
                            onClose={handleCloseDialog}
                        />
                    )}
                </DialogContent>
                <DialogActions>
                    <Button onClick={handleCloseDialog} color="look2xBlue">
                        {t('common.close', 'Close')}
                    </Button>
                    <Button
                        variant="contained"
                        color="look2xBlue"
                        onClick={handleOpenLearningPage}
                        disabled={!selectedItem?.templateId || !customerId}
                    >
                        {t('demoMail.toLearningPage', 'Zur Lernseite')}
                    </Button>
                </DialogActions>
            </Dialog>
            
        </>
    )
}
